<template>
  <form @submit.prevent="handleSubmit" data-testid="site-templates-form">
    <div class="form-field">
      <input-text
        elem-id="siteTemplateFormulario"
        v-model="formData.formulario"
        :label="t('Formulário')"
        :placeholder="t('expansao')"
        type="text"
        required
        fluid
        :disabled="isEditMode"
        data-testid="site-templates-formulario-input"
      />
      <!-- Read-only while editing: the slug is the key the site looks the
           template up by, so changing it here would silently orphan the form
           that is already sending through this row. -->
      <small v-if="isEditMode" class="field-hint" data-testid="site-templates-formulario-hint">
        {{ t('O formulário não pode ser alterado depois de criado.') }}
      </small>
      <small v-if="errors.formulario" class="error-message" data-testid="site-templates-formulario-error">{{ errors.formulario }}</small>
    </div>

    <div class="form-field">
      <input-text
        elem-id="siteTemplateAssunto"
        v-model="formData.assunto"
        :label="t('Assunto do e-mail')"
        type="text"
        required
        fluid
        data-testid="site-templates-assunto-input"
      />
      <small v-if="errors.assunto" class="error-message" data-testid="site-templates-assunto-error">{{ errors.assunto }}</small>
    </div>

    <div class="form-field">
      <input-text-area
        elem-id="siteTemplateCorpo"
        v-model="formData.corpo"
        :label="t('Corpo do e-mail')"
        :rows="14"
        fluid
        required
        data-testid="site-templates-corpo-input"
      />
      <small v-if="errors.corpo" class="error-message" data-testid="site-templates-corpo-error">{{ errors.corpo }}</small>

      <!-- The site expands these at send time, against the answers it just
           collected. Nothing here renders them, so the panel has to say which
           ones exist — a misspelled marker saves clean and mails literal text. -->
      <div class="placeholders-help" data-testid="site-templates-placeholders-help">
        <span class="placeholders-title">{{ t('Marcadores disponíveis') }}</span>
        <ul class="placeholders-list">
          <li v-for="marcador in marcadores" :key="marcador.chave" :data-testid="`site-templates-placeholder-${marcador.id}`">
            <code>{{ marcador.chave }}</code>
            <span class="placeholders-desc">{{ marcador.descricao }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="form-field">
      <input-text-area
        elem-id="siteTemplateDestinatarios"
        v-model="formData.destinatarios"
        :label="t('Destinatários')"
        :placeholder="t('Separe os endereços por vírgula')"
        :rows="2"
        fluid
        required
        data-testid="site-templates-destinatarios-input"
      />
      <small v-if="errors.destinatarios" class="error-message" data-testid="site-templates-destinatarios-error">{{ errors.destinatarios }}</small>
    </div>

    <div class="form-field">
      <input-text-area
        elem-id="siteTemplateCopias"
        v-model="formData.copias"
        :label="t('Cópias')"
        :placeholder="t('Separe os endereços por vírgula')"
        :rows="2"
        fluid
        data-testid="site-templates-copias-input"
      />
    </div>

    <div class="form-field">
      <input-toggle
        elem-id="siteTemplateAtivo"
        v-model="formData.ativo"
        :label="t('Ativo')"
        data-testid="site-templates-ativo-toggle"
      />
      <small class="field-hint">{{ t('Quando desativado, o site não envia o e-mail deste formulário.') }}</small>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from '../../inputs/InputText.vue';
import InputTextArea from '../../inputs/InputTextArea.vue';
import InputToggle from '../../inputs/InputToggle.vue';
import { useSiteTemplates } from '../../composables/useSiteTemplates';
import type { SiteTemplates_SalvarInput } from '../../contracts/SiteTemplates_SalvarInput';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type TemplateId = number | string | null;

interface ApiError extends Error {
  response?: {
    data?: {
      error?: string;
    };
  };
}

const props = withDefaults(defineProps<{
  templateId?: TemplateId;
  isEditMode?: boolean;
}>(), {
  templateId: null,
  isEditMode: false
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
  'validation-failed': [];
}>();

const { t } = useI18n();

const { fetchTemplate, salvarTemplate } = useSiteTemplates();

const templateIdNumber = computed<number | null>(() => {
  if (!props.templateId) return null;
  return typeof props.templateId === 'string' ? parseInt(props.templateId, 10) : props.templateId;
});

interface FormShape {
  formulario: string;
  assunto: string;
  corpo: string;
  destinatarios: string;
  copias: string;
  ativo: boolean;
}

const buildEmptyForm = (): FormShape => ({
  formulario: '',
  assunto: '',
  corpo: '',
  destinatarios: '',
  copias: '',
  ativo: true
});

const formData = reactive<FormShape>(buildEmptyForm());

const errors = reactive<{ formulario: string; assunto: string; corpo: string; destinatarios: string }>({
  formulario: '',
  assunto: '',
  corpo: '',
  destinatarios: ''
});

const isLoading = ref<boolean>(false);

// The markers the site knows how to expand. `{{respostas}}` is the whole
// answer table, the rest are single fields of the submitted form.
const marcadores = computed(() => [
  { id: 'nome', chave: '{{nome}}', descricao: t('Nome de quem preencheu') },
  { id: 'email', chave: '{{email}}', descricao: t('E-mail informado') },
  { id: 'telefone', chave: '{{telefone}}', descricao: t('Telefone informado') },
  { id: 'cidade', chave: '{{cidade}}', descricao: t('Cidade informada') },
  { id: 'estado', chave: '{{estado}}', descricao: t('Estado informado') },
  { id: 'pais', chave: '{{pais}}', descricao: t('País informado') },
  { id: 'faixa_investimento', chave: '{{faixa_investimento}}', descricao: t('Faixa de investimento informada') },
  { id: 'mensagem', chave: '{{mensagem}}', descricao: t('Mensagem escrita pelo visitante') },
  { id: 'respostas', chave: '{{respostas}}', descricao: t('Todas as respostas do formulário, em bloco') }
]);

const loadForEdit = async (): Promise<void> => {
  if (!templateIdNumber.value) {
    logWarn('SiteTemplatesForm', 'loadForEdit', 'No templateId provided');
    return;
  }

  log('SiteTemplatesForm', 'loadForEdit', 'Loading template for edit', templateIdNumber.value);
  isLoading.value = true;

  try {
    const template = await fetchTemplate(templateIdNumber.value);
    log('SiteTemplatesForm', 'loadForEdit', 'Loaded template data', template);

    if (!template) {
      throw new Error('Dados do template não encontrados');
    }

    formData.formulario = template.formulario || '';
    formData.assunto = template.assunto || '';
    formData.corpo = template.corpo || '';
    formData.destinatarios = template.destinatarios || '';
    formData.copias = template.copias || '';
    formData.ativo = template.ativo !== false;

    clearErrors();
  } catch (err) {
    const error = err as ApiError;
    logError('SiteTemplatesForm', 'loadForEdit', 'Error loading template', {
      templateId: templateIdNumber.value,
      error: error.message,
      response: error.response
    });

    const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar template') + ': ' + errorMessage);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SiteTemplatesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (formData.formulario.trim() === '') {
    errors.formulario = t('Campo obrigatório');
    isValid = false;
  }

  if (formData.assunto.trim() === '') {
    errors.assunto = t('Campo obrigatório');
    isValid = false;
  }

  if (formData.corpo.trim() === '') {
    errors.corpo = t('Campo obrigatório');
    isValid = false;
  }

  // A template with nobody to mail saves clean and sends nothing — the one
  // failure mode of this screen that is invisible until someone complains.
  if (formData.destinatarios.trim() === '') {
    errors.destinatarios = t('Campo obrigatório');
    isValid = false;
  }

  log('SiteTemplatesForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.formulario = '';
  errors.assunto = '';
  errors.corpo = '';
  errors.destinatarios = '';
};

const resetForm = (): void => {
  log('SiteTemplatesForm', 'resetForm', 'Resetting form');
  Object.assign(formData, buildEmptyForm());
  clearErrors();
};

const buildPayload = (): SiteTemplates_SalvarInput => {
  const payload: SiteTemplates_SalvarInput = {
    formulario: formData.formulario.trim(),
    assunto: formData.assunto.trim(),
    corpo: formData.corpo,
    // Posted as typed: the backend normalizes the separators, and re-splitting
    // them here would round-trip something other than what was written.
    destinatarios: formData.destinatarios,
    copias: formData.copias,
    ativo: formData.ativo
  };

  if (props.isEditMode && templateIdNumber.value) {
    payload.id = templateIdNumber.value;
  }

  return payload;
};

const handleSubmit = async (): Promise<void> => {
  log('SiteTemplatesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SiteTemplatesForm', 'handleSubmit', 'Validation failed');
    emit('validation-failed');
    return;
  }

  isLoading.value = true;

  try {
    const payload = buildPayload();
    log('SiteTemplatesForm', 'handleSubmit', 'Saving template', payload);
    await salvarTemplate(payload);
    eventBus.emit('globalMessage.success', props.isEditMode ? t('Template atualizado com sucesso') : t('Template criado com sucesso'));

    emit('submit-success');
    resetForm();
  } catch (err) {
    const error = err as Error;
    logError('SiteTemplatesForm', 'handleSubmit', 'Error submitting form', error);
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar template') + ': ' + error.message
        : t('Erro ao criar template') + ': ' + error.message
    );
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.templateId, async (newId) => {
  log('SiteTemplatesForm', 'watch', 'templateId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

onMounted(() => {
  log('SiteTemplatesForm', 'mounted', 'Component mounted', {
    templateId: props.templateId,
    templateIdNumber: templateIdNumber.value,
    isEditMode: props.isEditMode
  });

  if (props.isEditMode && templateIdNumber.value) {
    void loadForEdit();
  }
});

defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.field-hint {
  color: var(--p-text-muted-color);
  font-size: 0.8125rem;
}

.placeholders-help {
  margin-top: 0.5rem;
  padding: 12px;
  border-radius: 6px;
  background: var(--p-surface-100);
}

.placeholders-title {
  font-weight: 600;
  font-size: 0.875rem;
}

.placeholders-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 4px 16px;
  margin: 8px 0 0 0;
  padding-left: 18px;
  font-size: 0.8125rem;
}

.placeholders-desc {
  margin-left: 8px;
  color: var(--p-text-muted-color);
}
</style>
